import { v4 as uuid } from 'uuid';
import { Viewer } from '../../viewer/viewer';
import { PointoramaWallSketch } from '../PointoramaWallSketch';
import {
  PropertyValueType,
  getAnnotationNameProperty,
  getSketchObjectSourceStructureIdProperty,
  getStructureHeightProperty,
  getStructureKindProperty,
  getStructurePolygonProperty,
  type Coordinate,
} from '@pointorama/potree-shared';
import { StructureKind } from 'shared-types';
import { closestPointOnEdge } from './arcGeometry';
import { paramPropertyIdentifier } from '../../sketchObjects/paramSchema';
import type { SketchObjectRequest } from '../../sketchObjects/constraints/types';

/**
 * Replicate every floorplan STRUCTURE (column/beam) on a floor into its layout
 * equivalent — a SKETCH_OBJECT of type `column` or `beam` — as part of the
 * floor→sketch conversion (the structure counterpart of
 * `replicateOpeningSketchesForFloor`).
 *
 * Geometry: the structure's footprint polygon is reduced to its minimum-area
 * bounding rectangle. COLUMNs become POINT-anchored objects at the rectangle
 * center, rotated to the rectangle orientation. BEAMs become LINE-anchored
 * runs along the rectangle's long axis whose endpoints are SMART-CONNECTED to
 * the wall sketch: an endpoint near a wall snaps onto that wall's centerline,
 * so the converted beam meets its supporting walls exactly.
 *
 * Bounds: the structure's vertical extent survives as the type's bound
 * params — `height` (the structure's extrusion height) for both kinds, plus
 * the beam's `elevation` (underside above the floor, derived from the
 * polygon's base Z exactly like the structure's own label does).
 *
 * Idempotent: a structure already replicated (a sketch object carries its id
 * in `sketchobject_source_structure_id`) is skipped, so re-running the
 * conversion never duplicates. The original STRUCTURE is left untouched (the
 * floorplan persona keeps using it). Names carry over so user renames survive.
 *
 * Annotation creation happens in React; this dispatches one
 * `request_sketch_object` event per new object.
 */
export function replicateStructureSketchObjectsForFloor(viewer: Viewer, wallSketch: PointoramaWallSketch): void {
  const groupIdentifier = wallSketch.groupIdentifier;
  if (!groupIdentifier) return;
  const floorZ = wallSketch.getFloorHeight();

  // Already-replicated source ids (from live scene objects + cached annotations).
  const alreadyReplicated = new Set<string>();
  for (const so of viewer.scene.sketchObjects) {
    try {
      const src = getSketchObjectSourceStructureIdProperty(so.identifier).value;
      if (src) alreadyReplicated.add(src);
    } catch {
      // property service not ready — treat as not replicated
    }
  }

  for (const structure of viewer.scene.structures) {
    if (structure.groupIdentifier !== groupIdentifier) continue;
    if (alreadyReplicated.has(structure.identifier)) continue;

    let polygon: Coordinate[];
    let height: number;
    let kind: StructureKind;
    let name = '';
    try {
      polygon = getStructurePolygonProperty(structure.identifier).value;
      height = getStructureHeightProperty(structure.identifier).value;
      kind = getStructureKindProperty(structure.identifier).value;
      name = getAnnotationNameProperty(structure.identifier).value || '';
    } catch {
      continue;
    }
    if (!polygon || polygon.length < 3) continue;

    const rect = minAreaFootprintRect(polygon);
    if (!rect) continue;

    const identifier = `sketch_object_${groupIdentifier}_${uuid()}`;
    const common = { identifier, groupIdentifier, name: name || undefined, sourceStructureId: structure.identifier, wall: null };

    let request: SketchObjectRequest;
    if (kind === StructureKind.BEAM) {
      // The beam's underside above the floor — same derivation as the
      // structure's own elevation label (base ring Z − floor plane Z).
      const elevation = Math.max(0, polygon[0].z - floorZ);
      // Endpoints along the rectangle's long axis, each smart-connected onto
      // a nearby wall centerline so the beam meets its supporting walls.
      const rawA = { x: rect.center.x - (rect.dir.x * rect.length) / 2, y: rect.center.y - (rect.dir.y * rect.length) / 2 };
      const rawB = { x: rect.center.x + (rect.dir.x * rect.length) / 2, y: rect.center.y + (rect.dir.y * rect.length) / 2 };
      const a = snapPointToWallCenterline(wallSketch, rawA);
      const b = snapPointToWallCenterline(wallSketch, rawB);
      const length = Math.hypot(b.x - a.x, b.y - a.y);
      if (length < 1e-4) continue;
      request = {
        ...common,
        typeId: 'beam',
        width: length,
        depth: rect.width,
        anchor: [
          { x: a.x, y: a.y, z: floorZ },
          { x: b.x, y: b.y, z: floorZ },
        ],
        rotation: Math.atan2(b.y - a.y, b.x - a.x),
        paramValues: [
          { identifier: paramPropertyIdentifier('beam', 'height'), value: height, valueType: PropertyValueType.FLOAT },
          { identifier: paramPropertyIdentifier('beam', 'elevation'), value: elevation, valueType: PropertyValueType.FLOAT },
        ],
      };
    } else {
      request = {
        ...common,
        typeId: 'column',
        width: rect.length,
        depth: rect.width,
        anchor: [{ x: rect.center.x, y: rect.center.y, z: floorZ }],
        rotation: Math.atan2(rect.dir.y, rect.dir.x),
        paramValues: [
          { identifier: paramPropertyIdentifier('column', 'shape'), value: 'rectangular', valueType: PropertyValueType.SELECT },
          { identifier: paramPropertyIdentifier('column', 'height'), value: height, valueType: PropertyValueType.FLOAT },
        ],
      };
    }

    alreadyReplicated.add(structure.identifier);
    viewer.scene.dispatchEvent({ type: 'request_sketch_object', value: request });
  }
}

/** Reach (m) within which a converted beam endpoint snaps onto a wall
 *  centerline. Generous: a detected beam footprint usually stops at the wall
 *  FACE, so the gap to the centerline is about half the wall thickness. */
const BEAM_ENDPOINT_SNAP_REACH = 0.5;

/**
 * Smart-connect a beam endpoint to the wall sketch: the closest point on the
 * nearest wall edge centerline (arc-aware), when one is within
 * {@link BEAM_ENDPOINT_SNAP_REACH}; the raw point otherwise.
 */
function snapPointToWallCenterline(wallSketch: PointoramaWallSketch, point: { x: number; y: number }): { x: number; y: number } {
  let best: { x: number; y: number } | null = null;
  let bestDist = BEAM_ENDPOINT_SNAP_REACH;
  for (const edge of wallSketch.getEdges()) {
    const a = wallSketch.getNode(edge.startNodeId);
    const b = wallSketch.getNode(edge.endNodeId);
    if (!a || !b) continue;
    const foot = closestPointOnEdge(point, { x: a.position.x, y: a.position.y }, { x: b.position.x, y: b.position.y }, edge.bulge ?? 0);
    if (foot.distance < bestDist) {
      bestDist = foot.distance;
      best = { x: foot.point.x, y: foot.point.y };
    }
  }
  return best ?? point;
}

export interface FootprintRect {
  center: { x: number; y: number };
  /** Unit direction of the LONG side. */
  dir: { x: number; y: number };
  /** Extent along `dir` (the long side, ≥ `width`). */
  length: number;
  /** Extent across `dir` (the short side). */
  width: number;
}

/**
 * Minimum-area bounding rectangle of the footprint polygon, oriented so the
 * long side is the primary axis. Candidate orientations are the polygon's own
 * edge directions — exact for convex footprints (the classic rotating-calipers
 * property) and a solid fit for the near-rectangular shapes structures are in
 * practice.
 */
export function minAreaFootprintRect(polygon: Coordinate[]): FootprintRect | null {
  let best: { ux: number; uy: number; minU: number; maxU: number; minV: number; maxV: number; area: number } | null = null;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len = Math.hypot(ex, ey);
    if (len < 1e-9) continue;
    const ux = ex / len;
    const uy = ey / len;
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const p of polygon) {
      const u = p.x * ux + p.y * uy;
      const v = -p.x * uy + p.y * ux;
      minU = Math.min(minU, u);
      maxU = Math.max(maxU, u);
      minV = Math.min(minV, v);
      maxV = Math.max(maxV, v);
    }
    const area = (maxU - minU) * (maxV - minV);
    if (!best || area < best.area) best = { ux, uy, minU, maxU, minV, maxV, area };
  }
  if (!best) return null;

  const cu = (best.minU + best.maxU) / 2;
  const cv = (best.minV + best.maxV) / 2;
  // Back from (u, v) axes to world: p = u·U + v·V with V = U rotated 90° CCW.
  const center = { x: cu * best.ux - cv * best.uy, y: cu * best.uy + cv * best.ux };
  const du = best.maxU - best.minU;
  const dv = best.maxV - best.minV;
  if (du < 1e-9 && dv < 1e-9) return null;
  if (du >= dv) return { center, dir: { x: best.ux, y: best.uy }, length: du, width: dv };
  // Long side is across the candidate edge — rotate the primary axis 90°.
  return { center, dir: { x: -best.uy, y: best.ux }, length: dv, width: du };
}
